import { FC, useEffect, useRef, useState } from 'react';
import { PinCode } from '../../components/PinCode';
import { AllieButton, AllieText } from '../../../components';
import { AllieCloseOutlineIcon } from '../../../styling/icons';
import { LoginPinFooter, LoginPinHeader, LoginPinWrapper } from './LoginPin.styles';
import { keyDownSubmit } from '../common/helpers';

const LENGTH_PIN = 6;

export interface LoginPinProps {
  onSubmit: (pin: string) => Promise<void>;
  onCancel: () => void;
  onClose?: () => void;
  labelCancel: string;
  labelConfirm: string;
  labelTitle: string;
  disabledConfirm?: boolean;
  showClose?: boolean;
  errorMessage?: string;
  showError?: boolean;
  showLabel: string;
  hideLabel: string;
}

export const LoginPin: FC<LoginPinProps> = ({
  labelTitle,
  labelCancel,
  labelConfirm,
  onSubmit,
  onCancel,
  onClose,
  disabledConfirm,
  showClose = true,
  errorMessage,
  showError,
  showLabel,
  hideLabel,
}) => {
  const [pin, setPin] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loginButtonRef = useRef<HTMLIonButtonElement>(null);

  const isDisabled = pin.length < LENGTH_PIN || disabledConfirm;

  const submit = async (PIN: string) => {
    setIsLoading(true);
    await onSubmit(PIN);
    setIsLoading(false);
  };

  const onKeyDown = keyDownSubmit(Boolean(isDisabled), () => submit(pin));

  useEffect(() => {
    if (!isDisabled) {
      loginButtonRef.current?.focus();
    }
  }, [isDisabled]);

  return (
    <LoginPinWrapper>
      <LoginPinHeader>
        <AllieText type="h3" color="neutral-70" weight={600}>
          {labelTitle}
        </AllieText>
        {showClose && <AllieCloseOutlineIcon onclick={onClose} size="large" />}
      </LoginPinHeader>
      <div className="content">
        <PinCode
          fields={LENGTH_PIN}
          onChange={(val) => setPin(val)}
          showLabel={showLabel}
          hideLabel={hideLabel}
        />
        {showError && (
          <div style={{ marginTop: 12, padding: '0 22px' }}>
            <AllieText color="error-60">{errorMessage}</AllieText>
          </div>
        )}
      </div>
      <LoginPinFooter>
        <AllieButton
          onclick={onCancel}
          allieSize="small"
          allieType="secondary"
          corner="rounded-edges"
        >
          {labelCancel}
        </AllieButton>
        <AllieButton
          tabIndex={1}
          onclick={() => submit(pin)}
          allieSize="small"
          corner="rounded-edges"
          disabled={isDisabled}
          onKeyDown={onKeyDown}
          ref={loginButtonRef}
          loading={isLoading}
        >
          {labelConfirm}
        </AllieButton>
      </LoginPinFooter>
    </LoginPinWrapper>
  );
};
